import { useState } from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import { Valuation } from '../../api/types'
import { apiError } from '../../api/errors'
import { useOpsApi } from '../../api/context'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { formatDateTime } from '../platby/format'
import { formatDate, formatUnits } from './format'

interface InstrumentPriceCardProps {
  latestValuation?: Valuation
  canUpdate: boolean
  onUpdated: () => void
}

/**
 * Cena instrumentu v jádru (graf, hodnota portfolií klientů) se ocenění modulu NEMĚNÍ (Michal 19. 10. 2026).
 * Ke konci měsíce ji CEO aktualizuje tady podle posledního ocenění.
 */
export function InstrumentPriceCard({ latestValuation, canUpdate, onUpdated }: InstrumentPriceCardProps) {
  const api = useOpsApi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const v = latestValuation
  const modulePrice = v ? Math.round(v.unitPrice * 10000) / 10000 : null
  const same = v && v.instrumentPrice != null && modulePrice === v.instrumentPrice

  const handleUpdate = async () => {
    if (!v) return
    const ok = window.confirm(
      `Zapsat cenu ${formatUnits(modulePrice ?? v.unitPrice)} jako cenu instrumentu k ${formatDate(v.date)}?\n` +
        'Klientům se změní graf a hodnota portfolia (po nočním přepočtu).'
    )
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await api.updateInstrumentPrice(v.id)
      onUpdated()
    } catch (err) {
      setError(apiError(err, 'Aktualizace ceny instrumentu selhala'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard
      title="Cena instrumentu"
      info={
        <InfoHint
          title="Cena jednotky × cena instrumentu"
          lines={[
            'Ocenění v modulu mění jen cenu jednotky pro výpočet jednotek (vklady, odkupy, fee).',
            'Cena instrumentu v Portiliu (graf, hodnota portfolia klientů) se mění jen tímto tlačítkem — typicky ke konci měsíce.',
            'Zapíše se cena z posledního ocenění (4 desetinná místa) pro obě třídy fondu k datu ocenění; existující cena k tomu dni se přepíše.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      {!v ? (
        <Typography variant="body2" color="text.secondary">
          Fond zatím nemá ocenění.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2">
            Poslední ocenění {formatDate(v.date)}: cena jednotky <b>{formatUnits(v.unitPrice)}</b>
          </Typography>
          <Typography variant="body2">
            Cena instrumentu k {formatDate(v.date)}:{' '}
            <b>{v.instrumentPrice != null ? formatUnits(v.instrumentPrice) : 'žádná'}</b>
            {v.instrumentPriceAt ? ` (aktualizováno ${formatDateTime(v.instrumentPriceAt)})` : ''}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button variant="contained" onClick={handleUpdate} disabled={!canUpdate || busy || !!same}>
              Aktualizovat cenu instrumentu
            </Button>
            {!canUpdate && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Jen CEO.
              </Typography>
            )}
            {same && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Cena instrumentu odpovídá ocenění.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </SectionCard>
  )
}
